import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import _get from "lodash/get";
import { ChevronRightIcon, ChevronLeftIcon } from "@heroicons/react/solid";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { Formik, Field } from "formik";

import OfficialShareAllocation from "./OfficialShareAllocation";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import {
  getCompanySecretaryDocumentData,
  getDocumentData,
} from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm/documentData";
import FormFieldsCard from "components/lib/Global/FormFieldsCard";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import Tabs from "components/lib/Shared/Tabs";
import { INDIVIDUALS } from "components/PLCTABS/SFLdata/config/constants";
import CommentsList from "components/lib/Global/CommentsList";
import { addComment, editComment } from "state/slices/comment";
import { updateCompanyOfficialAsync } from "state/slices/tables/companyOfficialsSlice";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";
import { useToast } from "hooks/useToast";
import { getFormFields } from "utils";
import { getAllBeneficialOwners } from "state/slices/tables/beneficialOwnersTableSlice";
import { setShares } from "state/slices/tables/sharesAllocationTable";
import OfficialBeneficialOwnership from "./OfficialBeneficialOwnership";
import { resetSharesTable } from "state/slices/tables/sharesAllocationTable";
import { useJointShareholders } from "hooks/useJointShareholders";
import { editJointShareholder } from "state/slices/tables/jointShareholderTable";
import ChangeDocuments from "./ChangeDocuments";
import { Constants } from "config/constants";

function SubRowAsync(props) {
  const {
    visibleColumns,
    row,
    data,
    isChangesList,
    originalCompanyOfficials,
    handleBackBtn,
    syncChangesToBOTab,
    currentTab = null,
    isRegistrar = false,
    remainderAmounts,
    transactions,
    shareholderPercentageChanges,
  } = props;

  const history = useHistory();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const dispatch = useDispatch();

  let isViewMode =
    isChangesList || history.location.pathname.split("/").pop() === "view";

  const { comments } = useSelector((state) => state.commentsSlice);
  const currentUser = useSelector((state) => state.userSlice);
  const { company } = useSelector((state) => state.companySlice);
  const isApplicant = currentUser?._id === company?.createdBy?._id;
  const { beneficialOwners } = useSelector(
    (state) => state.beneficialOwnersTableSlice
  );
  const isCompanyDashboard =
    history.location.pathname.split("/").pop() === "dashboard";

  const changeDocumentsComments = comments.filter(
    (comment) => comment.formName === "uploadChangeDocuments"
  );

  const stakeholderComments = comments.filter(
    (comment) =>
      comment?.officialId &&
      comment.officialId === row.original._id &&
      comment?.tabName &&
      comment.tabName === currentTab
  );

  const generalInformationComments = stakeholderComments.filter(
    (comment) =>
      comment.formName === "identificationDetails" ||
      comment.formName === "names"
  );
  const residentialDetailsComments = stakeholderComments.filter(
    (comment) =>
      comment.formName === "postalAddress" ||
      comment.formName === "electronicAddress" ||
      comment.formName === "residentialAddressDetails" ||
      comment.formName === "isNonResident"
  );
  const officialShareAllocationComments = stakeholderComments.filter(
    (comment) => comment.formName === "officialShareAllocation"
  );
  const officialBeneficialOwnershipComments = stakeholderComments.filter(
    (comment) => comment.formName === "beneficialOwnershipForm"
  );
  const fileUploadComments = stakeholderComments.filter(
    (comment) => comment.formName === "uploadDocuments"
  );

  // Helper function to check if a Director has allocated shares
  const hasDirectorAllocatedShares = useMemo(() => {
    if (!isChangesList || row.original.designation !== "Director") {
      return false;
    }

    // Check if there are shareCapitalChanges with allocations for this official
    let hasAllocatedShares = false;

    if (transactions && Array.isArray(transactions)) {
      for (const transaction of transactions) {
        if (
          transaction.shareCapitalChanges &&
          Array.isArray(transaction.shareCapitalChanges)
        ) {
          for (const shareChange of transaction.shareCapitalChanges) {
            if (
              shareChange.allocations &&
              Array.isArray(shareChange.allocations)
            ) {
              const hasAllocation = shareChange.allocations.some(
                (allocation) =>
                  allocation.officialId === row.original._id &&
                  allocation.allocatedShares > 0
              );
              if (hasAllocation) {
                hasAllocatedShares = true;
                break;
              }
            }
          }
          if (hasAllocatedShares) break;
        }
      }
    }

    // Fallback: check if the official has allocatedShares directly
    if (
      !hasAllocatedShares &&
      row.original.allocatedShares &&
      row.original.allocatedShares > 0
    ) {
      hasAllocatedShares = true;
    }

    return hasAllocatedShares;
  }, [
    isChangesList,
    row.original.designation,
    row.original._id,
    row.original.allocatedShares,
    transactions,
  ]);

  const tabs = useMemo(() => {
    let tempTabs = [];

    if (
      row?.original.cessationDate &&
      row.original.designation !== "Director" &&
      !hasDirectorAllocatedShares
    ) {
      tempTabs = [
        {
          id: "generalInformation",
          label: "General information",
          commentsCount: generalInformationComments?.length,
        },
      ];
    } else if (
      ["Director", "Authorized Person", "Company Secretary"].includes(
        row.original.designation
      ) &&
      !(row.original.designation === "Director" && hasDirectorAllocatedShares)
    ) {
      // Standard Director/Authorized Person/Company Secretary tabs (without share allocation)
      tempTabs = [
        {
          id: "generalInformation",
          label: "General information",
          commentsCount: generalInformationComments?.length,
        },
        {
          id: "residentialDetails",
          label: "Address details",
          commentsCount: residentialDetailsComments?.length,
        },
        {
          id: "uploadDocuments",
          label: "File Uploads",
          commentsCount: fileUploadComments?.length,
        },
      ];
    } else if (row.original.designation === "Joint Shareholder") {
      const isSeniorJointShareholder = row.index === 0;
      tempTabs = [
        {
          id: "generalInformation",
          label: "General information",
          commentsCount: generalInformationComments?.length,
        },
        {
          id: "residentialDetails",
          label: "Address details",
          commentsCount: residentialDetailsComments?.length,
        },
        isSeniorJointShareholder
          ? {
              id: "officialShareAllocation",
              label: isChangesList ? "Share Allocation" : "Share information",
              commentsCount: officialShareAllocationComments?.length,
            }
          : null,
        isSeniorJointShareholder
          ? {
              id: "officialBeneficialOwnership",
              label: "Beneficial ownership",
              commentsCount: officialBeneficialOwnershipComments?.length,
            }
          : null,
        {
          id: "uploadDocuments",
          label: "File Uploads",
          commentsCount: fileUploadComments?.length,
        },
      ].filter(Boolean);
    } else {
      tempTabs = [
        {
          id: "generalInformation",
          label: "General information",
          commentsCount: generalInformationComments?.length,
        },
        {
          id: "residentialDetails",
          label: "Address details",
          commentsCount: residentialDetailsComments?.length,
        },
        {
          id: "officialShareAllocation",
          label: isChangesList ? "Share Allocation" : "Share information",
          commentsCount: officialShareAllocationComments?.length,
        },
        {
          id: "officialBeneficialOwnership",
          label: "Beneficial ownership",
          commentsCount: officialBeneficialOwnershipComments?.length,
        },
        {
          id: "uploadDocuments",
          label: "File Uploads",
          commentsCount: fileUploadComments?.length,
        },
      ];
    }
    if (currentTab === "beneficialOwnership") {
      tempTabs = [
        {
          id: "generalInformation",
          label: "General information",
          commentsCount: generalInformationComments?.length,
        },
        {
          id: "residentialDetails",
          label: "Address details",
          commentsCount: residentialDetailsComments?.length,
        },
        {
          id: "officialBeneficialOwnership",
          label: "Beneficial ownership",
          commentsCount: officialBeneficialOwnershipComments?.length,
        },
        {
          id: "uploadDocuments",
          label: "File Uploads",
          commentsCount: fileUploadComments?.length,
        },
      ];
    }

    // Filter out uploadDocuments tab if isChangesList is true
    if (isChangesList) {
      tempTabs = tempTabs.filter((tab) => tab.id !== "uploadDocuments");
    }

    if (
      row?.original?.uploadChangeDocuments &&
      Object.keys(row.original.uploadChangeDocuments).length > 0 &&
      row.original.designation !== "Director" &&
      !hasDirectorAllocatedShares
    ) {
      tempTabs.push({
        id: "changeDocuments",
        label: "Change Documents",
      });
    }

    return tempTabs;
  }, [
    row.original.designation,
    row.original.cessationDate,
    row.original.uploadChangeDocuments,
    row.index,
    currentTab,
    generalInformationComments?.length,
    residentialDetailsComments?.length,
    fileUploadComments?.length,
    officialShareAllocationComments?.length,
    officialBeneficialOwnershipComments?.length,
    isChangesList,
    hasDirectorAllocatedShares,
  ]);

  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const { toast } = useToast(5000);
  const { syncBeneficialOwnersWithJointShareholders } = useJointShareholders();

  const goToPrevStep = () => {
    if (selectedTabIndex === 0 && handleBackBtn) handleBackBtn();
    else setSelectedTabIndex(selectedTabIndex - 1);
  };

  const goToNextStep = () => {
    setSelectedTabIndex(selectedTabIndex + 1);
  };

  const handleSaveComment = async (data) => {
    if (!company?._id) {
      toast(
        "error",
        "Something went wrong please reload the page and try again."
      );
      return;
    }
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            officialId: row.original._id,
            tabName: currentTab,
          },
        })
      );
    }
  };

  useEffect(() => {
    dispatch(setShares(row.original.sharesAllotted ?? []));
    return () => {
      dispatch(resetSharesTable());
    };
  }, [dispatch, row.original.sharesAllotted]);

  useEffect(() => {
    const tempSelectedTab = tabs[selectedTabIndex];
    if (tempSelectedTab) setSelectedTab(tempSelectedTab);
  }, [tabs, selectedTabIndex]);

  const isFirstStep = selectedTabIndex === 0;
  const isLastStep = tabs.length - 1 === selectedTabIndex;

  let dateOfBirth = _get(row.original.identificationDetails, "dateOfBirth");
  if (dateOfBirth) dateOfBirth = format(new Date(dateOfBirth), "yyyy/MM/dd");
  else dateOfBirth = "N/A";

  const [acceptingData, setAcceptingData] = useState(false);

  const officialIndex =
    currentTab === "stakeholderTab"
      ? props.officialIndex || row.index
      : beneficialOwners.findIndex(
          (official) => official._id === row.original._id
        );

  const officialSliceName =
    currentTab === "stakeholderTab"
      ? `companyOfficialsSlice.companyOfficials.items.${officialIndex}`
      : `beneficialOwnersTableSlice.beneficialOwners.${officialIndex}`;

  const companyOfficialEffectKey =
    currentTab === "stakeholderTab"
      ? "updateCompanyOfficialState"
      : "updateBeneficialOfficialState";

  const acceptData = async () => {
    if (!company?._id) {
      toast(
        "error",
        "Something went wrong please reload the page and try again."
      );
      return;
    }
    try {
      setAcceptingData(true);
      const updateOfficialResponse = await dispatch(
        updateCompanyOfficialAsync({
          officialId: row.original._id,
          data: {
            inviteStatus: "Approved",
            companyId: company?._id,
            idType: row?.original?.idType,
            initiatedToCompanyRegNumber: row.original.names.companyRegNumber,
          },
        })
      );
      if (syncChangesToBOTab) {
        dispatch(editJointShareholder(updateOfficialResponse.payload.official));
        syncBeneficialOwnersWithJointShareholders();
      }
      await dispatch(getAllBeneficialOwners({ companyId: company?._id }));
      row.toggleRowExpanded();
    } catch (error) {
    } finally {
      setAcceptingData(false);
    }
  };

  // Manual allotment inline control
  const [manualAllotShares, setManualAllotShares] = useState(0);
  const [isSavingManualAllot, setIsSavingManualAllot] = useState(false);
  const initiatedTransactions = useSelector(
    (state) => state.initiatedTransactionsSlice.initiatedTransactions
  );
  const handleManualAllotSubmit = async () => {
    try {
      setIsSavingManualAllot(true);
      const processType = [
        Constants.changeTypeEnum?.privateWithMoreThanOneClass ||
          "privateWithMoreThanOneClass",
        Constants.changeTypeEnum?.unallotedShares || "unallotedShares",
      ].includes(row.original?.processType)
        ? row.original?.processType
        : "unallotedShares";

      const tx = (initiatedTransactions?.items || []).find(
        (t) =>
          t.transactionType === Constants.TRANSACTION_TYPES.MANUAL &&
          t.processType === processType
      );
      if (!tx?._id) {
        toast("error", "No matching transaction found for manual allotment");
        return;
      }
      const classOfShares = row.original?.classOfShares;
      const existingChanges = tx.shareCapitalChanges || [];
      const existingChange = existingChanges.find(
        (c) => c.classOfShares === classOfShares
      ) || { classOfShares, sharesOnOffer: tx?.sharesOnOffer };
      const newAllocation = {
        officialId: row.original?._id,
        name: row.original?.fullName || "",
        currentShares: 0,
        proportion: "0",
        allocatedShares: Number(manualAllotShares) || 0,
      };
      const updatedAllocations = [
        ...(existingChange.allocations || []),
        newAllocation,
      ].map((alloc) => {
        // Remove legacy beneficialOwnership field to avoid CastErrors
        const { beneficialOwnership, ...rest } = alloc;
        return rest;
      });
      const totalAllocated = updatedAllocations.reduce(
        (sum, a) => sum + (Number(a.allocatedShares) || 0),
        0
      );
      const sharesOnOffer =
        existingChange.sharesOnOffer || row.original?.sharesOnOffer || 0;
      if (totalAllocated > sharesOnOffer) {
        toast("error", "Allocated shares exceed shares on offer");
        return;
      }
      const updatedChange = {
        ...existingChange,
        option: "manual",
        allocations: updatedAllocations,
      };
      const updatedShareCapitalChanges = [
        ...existingChanges.filter((c) => c.classOfShares !== classOfShares),
        updatedChange,
      ];
      await handleCompanyMaintenanceFormData(
        "existing-request",
        {
          companyId: company?._id,
          id: tx._id,
          formName: "initiateTransactionChange",
          shareCapitalChanges: updatedShareCapitalChanges,
        },
        company?._id
      );
      setManualAllotShares(0);
      toast("success", "Manual allotment saved");
    } catch (e) {
      toast("error", e?.message || "Failed to save manual allotment");
    } finally {
      setIsSavingManualAllot(false);
    }
  };

  return (
    <tr className="bg-[#EDF0F5]">
      <td colSpan={visibleColumns.length} className="px-9">
        <Tabs
          tabs={tabs}
          selectedTabIndex={selectedTabIndex}
          setSelectedTabIndex={setSelectedTabIndex}
        />
        {selectedTab.id === "appointer" && (
          <>
            {appointerComments && appointerComments.length > 0 && (
              <div className="px-8 pt-5 pb-6 mt-6 bg-white">
                <CommentsList
                  comments={appointerComments}
                  toggleCommentStatus={false}
                />
                <p className="mt-6 text-sm text-primary">
                  Comments: {appointerComments.length} of{" "}
                  {appointerComments.length}
                </p>
              </div>
            )}
            <FormFieldsCard
              handleSaveComment={handleSaveComment}
              fields={[
                {
                  name: "appointers",
                  value: Array.isArray(row.original?.appointerNames)
                    ? row.original.appointerNames
                        .map((item) => getFullName(item) || "N/A")
                        .join(", ")
                    : row.original?.appointerNames ?? "N/A",
                },
              ]}
              resolvedFields={_get(row.original, "resolvedFields")}
              addReviewComment={isViewMode ? false : true}
              hideResolveAllCheckbox={isViewMode}
              isReviewer={isReviewer}
              isApprove={isApprove}
              formName="appointers"
              formHelperKey={"individualOfficial"}
              updateEffectKey={companyOfficialEffectKey}
              updateEffectPath={officialSliceName}
              formId={_get(row.original, "_id")}
              comments={appointerComments.filter(
                (comment) => comment.formName === "appointers"
              )}
            />
          </>
        )}
        {selectedTab.id === "generalInformation" &&
          (row.original?.changeType ===
          Constants.TRANSACTION_CHANGE_TYPES.CESSATION ? (
            <div className="col-span-6 sm:col-span-3">
              <FormFieldsCard
                handleSaveComment={handleSaveComment}
                fields={getFormFields({
                  idType: row?.original?.idType,
                  formType: "cessation",
                  formValues: {
                    cessationDate: row?.original?.cessationDate,
                  },
                })}
                resolvedFields={_get(row.original, "resolvedFields")}
                addReviewComment={!isViewMode}
                hideResolveAllCheckbox={true}
                formName="cessationDate"
                formHelperKey={
                  currentTab === "stakeholderTab"
                    ? INDIVIDUALS.includes(row?.original?.idType)
                      ? "individualOfficial"
                      : "corporateOfficial"
                    : "beneficialOwner"
                }
                updateEffectKey={companyOfficialEffectKey}
                updateEffectPath={`${officialSliceName}`}
                formId={_get(row.original, "_id")}
                comments={generalInformationComments.filter(
                  (comment) => comment.formName === "individualOfficial"
                )}
                changeType={row?.original?.changeType}
              />
            </div>
          ) : isChangesList ? (
            <>
              {generalInformationComments &&
                generalInformationComments.length > 0 && (
                  <div className="px-8 pt-5 pb-6 mt-6 bg-white">
                    <CommentsList
                      comments={generalInformationComments}
                      toggleCommentStatus
                      applicantReviewer
                    />
                    <p className="mt-6 text-sm text-primary">
                      Comments: {generalInformationComments.length} of{" "}
                      {generalInformationComments.length}
                    </p>
                  </div>
                )}
              <FormFieldsCard
                handleSaveComment={handleSaveComment}
                fields={
                  getFormFields({
                    idType: row.original.idType,
                    formType: "names",
                    formValues: row.original.names || {},
                  }) || []
                }
                resolvedFields={
                  _get(row.original.names, "resolvedFields") || []
                }
                addReviewComment={!isViewMode}
                formName="names"
                formHelperKey="names"
                updateEffectKey={companyOfficialEffectKey}
                updateEffectPath={`${officialSliceName}.names`}
                formId={_get(row.original.names, "_id")}
                comments={generalInformationComments.filter(
                  (comment) => comment.formName === "names"
                )}
                changedFields={
                  getFormFields({
                    idType: row?.original?.idType,
                    formType: "names",
                    formValues: row?.original?.changedFields?.names || {},
                  }) || []
                }
                changeType={row?.original?.changeType}
              />
              {/* Date of Change field */}
              <FormFieldsCard
                handleSaveComment={handleSaveComment}
                fields={[
                  {
                    key: "Date of Change",
                    name: "dateOfChange",
                    value: row.original?.dateOfOffer
                      ? format(new Date(row.original.dateOfOffer), "yyyy/MM/dd")
                      : "N/A",
                  },
                ]}
                resolvedFields={_get(row.original, "resolvedFields")}
                addReviewComment={!isViewMode}
                hideResolveAllCheckbox={true}
                formName="dateOfChange"
                formHelperKey="dateOfChange"
                updateEffectKey={companyOfficialEffectKey}
                updateEffectPath={officialSliceName}
                formId={_get(row.original, "_id")}
                comments={generalInformationComments.filter(
                  (comment) => comment.formName === "dateOfChange"
                )}
              />
            </>
          ) : (
            <>
              {generalInformationComments &&
                generalInformationComments.length > 0 && (
                  <div className="px-8 pt-5 pb-6 mt-6 bg-white">
                    <CommentsList
                      comments={generalInformationComments}
                      toggleCommentStatus
                      applicantReviewer
                    />
                    <p className="mt-6 text-sm text-primary">
                      Comments: {generalInformationComments.length} of{" "}
                      {generalInformationComments.length}
                    </p>
                  </div>
                )}
              <FormFieldsCard
                handleSaveComment={handleSaveComment}
                fields={
                  getFormFields({
                    idType: row.original.idType,
                    formType: "names",
                    formValues: row.original.names || {},
                  }) || []
                }
                resolvedFields={
                  _get(row.original.names, "resolvedFields") || []
                }
                addReviewComment={!isViewMode}
                formName="names"
                formHelperKey="names"
                updateEffectKey={companyOfficialEffectKey}
                updateEffectPath={`${officialSliceName}.names`}
                formId={_get(row.original.names, "_id")}
                comments={generalInformationComments.filter(
                  (comment) => comment.formName === "names"
                )}
                changedFields={
                  getFormFields({
                    idType: row?.original?.idType,
                    formType: "names",
                    formValues: row?.original?.changedFields?.names || {},
                  }) || []
                }
                changeType={row?.original?.changeType}
              />
              <FormFieldsCard
                handleSaveComment={handleSaveComment}
                fields={
                  getFormFields({
                    idType: row?.original?.idType,
                    formType: "identificationDetails",
                    formValues: row?.original?.identificationDetails || {},
                  }) || []
                }
                resolvedFields={
                  _get(row.original.identificationDetails, "resolvedFields") ||
                  []
                }
                addReviewComment={!isViewMode}
                formName="identificationDetails"
                formHelperKey="identificationDetails"
                updateEffectKey={companyOfficialEffectKey}
                updateEffectPath={`${officialSliceName}.identificationDetails`}
                formId={_get(row.original.identificationDetails, "_id")}
                comments={generalInformationComments.filter(
                  (comment) => comment.formName === "identificationDetails"
                )}
                files={row?.original?.fileUploads ?? {}}
                changedFields={
                  getFormFields({
                    idType: row?.original?.idType,
                    formType: "identificationDetails",
                    formValues:
                      row?.original?.changedFields?.identificationDetails || {},
                  }) || []
                }
                changeType={row?.original?.changeType}
              />
              {[
                "Local Company",
                "Foreign Company",
                "Other e.g Parastatals",
              ].includes(row?.original?.idType) && (
                <FormFieldsCard
                  handleSaveComment={handleSaveComment}
                  addReviewComment={!isViewMode}
                  formName="identificationDetails"
                  formHelperKey="identificationDetails"
                  formId={_get(row.original.identificationDetails, "_id")}
                  comments={generalInformationComments.filter(
                    (comment) => comment.formName === "identificationDetails"
                  )}
                  updateEffectKey={companyOfficialEffectKey}
                  updateEffectPath={`${officialSliceName}.identificationDetails`}
                  resolvedFields={_get(
                    row.original.identificationDetails,
                    "resolvedFields"
                  )}
                  fields={
                    getFormFields({
                      idType: row?.original?.idType,
                      formType: "contactPersonInfo",
                      formValues: row?.original?.identificationDetails || {},
                    }) || []
                  }
                  changedFields={
                    getFormFields({
                      idType: row?.original?.idType,
                      formType: "contatPersonInfo",
                      formValues:
                        row?.original?.changedFields?.identificationDetails ||
                        {},
                    }) || []
                  }
                  changeType={row?.original?.changeType}
                />
              )}
            </>
          ))}
        {selectedTab.id === "residentialDetails" && (
          <>
            {residentialDetailsComments &&
              residentialDetailsComments.length > 0 && (
                <div className="px-8 pt-5 pb-6 mt-6 bg-white">
                  <CommentsList
                    comments={residentialDetailsComments}
                    toggleCommentStatus
                    applicantReviewer
                  />
                  <p className="mt-6 text-sm text-primary">
                    Comments: {residentialDetailsComments.length} of{" "}
                    {residentialDetailsComments.length}
                  </p>
                </div>
              )}
            {!isChangesList && row?.original?.idType === "Kenyan Citizen" && (
              <FormFieldsCard
                handleSaveComment={handleSaveComment}
                fields={[
                  {
                    key: "Non-Resident",
                    name: "isNonResident",
                    value: row.original?.isNonResident ?? "N/A",
                  },
                ]}
                resolvedFields={_get(row.original, "resolvedFields")}
                addReviewComment={!isViewMode}
                formName="isNonResident"
                formHelperKey={
                  currentTab === "stakeholderTab"
                    ? INDIVIDUALS.includes(row?.original?.idType)
                      ? "individualOfficial"
                      : "corporateOfficial"
                    : "beneficialOwner"
                }
                updateEffectKey={companyOfficialEffectKey}
                updateEffectPath={officialSliceName}
                formId={_get(row.original, "_id")}
                comments={residentialDetailsComments.filter(
                  (comment) => comment.formName === "isNonResident"
                )}
              />
            )}
            <FormFieldsCard
              handleSaveComment={handleSaveComment}
              fields={
                getFormFields({
                  idType:
                    row?.original?.isNonResident &&
                    row?.original?.isNonResident === "Yes"
                      ? "Foreigner"
                      : row?.original?.idType,
                  formType: "postalAddress",
                  formValues: row?.original?.postalAddress || {},
                }) || []
              }
              resolvedFields={
                _get(row.original.postalAddress, "resolvedFields") || []
              }
              addReviewComment={!isViewMode}
              formName="postalAddress"
              formHelperKey="postalAddress"
              updateEffectKey={companyOfficialEffectKey}
              updateEffectPath={`${officialSliceName}.postalAddress`}
              formId={_get(row.original.postalAddress, "_id")}
              comments={residentialDetailsComments.filter(
                (comment) => comment.formName === "postalAddress"
              )}
              changedFields={
                getFormFields({
                  idType: row?.original?.idType,
                  formType: "postalAddress",
                  formValues: row?.original?.changedFields?.postalAddress || {},
                }) || []
              }
              changeType={row?.original?.changeType}
            />
            {!isChangesList && (
              <>
                <FormFieldsCard
                  handleSaveComment={handleSaveComment}
                  fields={
                    getFormFields({
                      idType: row?.original?.idType,
                      formType: "electronicAddress",
                      formValues: row?.original?.electronicAddress || {},
                    }) || []
                  }
                  resolvedFields={
                    _get(row.original.electronicAddress, "resolvedFields") || []
                  }
                  addReviewComment={!isViewMode}
                  formName="electronicAddress"
                  formHelperKey="electronicAddress"
                  updateEffectKey={companyOfficialEffectKey}
                  updateEffectPath={`${officialSliceName}.electronicAddress`}
                  formId={_get(row.original.electronicAddress, "_id")}
                  comments={residentialDetailsComments.filter(
                    (comment) => comment.formName === "electronicAddress"
                  )}
                  changedFields={
                    getFormFields({
                      idType: row?.original?.idType,
                      formType: "electronicAddress",
                      formValues:
                        row?.original?.changedFields?.electronicAddress || {},
                    }) || []
                  }
                  changeType={row?.original?.changeType}
                />
                <FormFieldsCard
                  handleSaveComment={handleSaveComment}
                  fields={
                    getFormFields({
                      idType:
                        row?.original?.isNonResident &&
                        row?.original?.isNonResident === "Yes"
                          ? "Foreigner"
                          : row?.original?.idType,
                      formType: "residentialAddressDetails",
                      formValues:
                        row?.original?.residentialAddressDetails || {},
                    }) || []
                  }
                  resolvedFields={
                    _get(
                      row.original.residentialAddressDetails,
                      "resolvedFields"
                    ) || []
                  }
                  addReviewComment={!isViewMode}
                  formName="residentialAddressDetails"
                  formHelperKey="residentialAddressDetails"
                  updateEffectKey={companyOfficialEffectKey}
                  updateEffectPath={`${officialSliceName}.residentialAddressDetails`}
                  formId={_get(row.original.residentialAddressDetails, "_id")}
                  comments={residentialDetailsComments.filter(
                    (comment) =>
                      comment.formName === "residentialAddressDetails"
                  )}
                  changedFields={
                    getFormFields({
                      idType: row?.original?.idType,
                      formType: "residentialAddressDetails",
                      formValues:
                        row?.original?.changedFields
                          ?.residentialAddressDetails || {},
                    }) || []
                  }
                  changeType={row?.original?.changeType}
                />
              </>
            )}
          </>
        )}
        {selectedTab.id === "officialShareAllocation" && (
          <OfficialShareAllocation
            row={row}
            isViewMode={isViewMode}
            officialSliceName={officialSliceName}
            companyOfficialEffectKey={companyOfficialEffectKey}
            officialShareAllocationComments={officialShareAllocationComments}
            isChangesList={isChangesList}
            remainderAmounts={remainderAmounts}
          />
        )}
        {selectedTab.id === "officialBeneficialOwnership" && (
          <OfficialBeneficialOwnership
            row={row}
            officialBeneficialOwnershipComments={
              officialBeneficialOwnershipComments
            }
            companyOfficialEffectKey={companyOfficialEffectKey}
            officialSliceName={officialSliceName}
            handleSaveComment={handleSaveComment}
            beneficialOwners={beneficialOwners}
            isViewMode={isViewMode}
            isChangesList={isChangesList}
            shareholderPercentageChanges={shareholderPercentageChanges}
          />
        )}
        {selectedTab.id === "uploadDocuments" && !isChangesList && (
          <>
            {fileUploadComments && fileUploadComments.length > 0 && (
              <div className="px-8 pt-5 pb-6 mt-6 bg-white">
                <CommentsList
                  comments={fileUploadComments}
                  toggleCommentStatus
                  applicantReviewer
                />
                <p className="mt-6 text-sm text-primary">
                  Comments: {fileUploadComments.length} of{" "}
                  {fileUploadComments.length}
                </p>
              </div>
            )}
            <div className="px-8 pt-5 pb-6 mt-6 bg-white">
              <h3 className="mb-2 text-lg">Uploaded incorporation forms</h3>
              <Formik
                enableReinitialize
                initialValues={{
                  fileUploads: row.original.fileUploads,
                }}
                onSubmit={() => null}
              >
                {() => {
                  return (
                    <div className="pl-4">
                      <Field
                        name="fileUploads"
                        component={FileUploadForm}
                        documentData={
                          row.original.designation === "Company Secretary"
                            ? getCompanySecretaryDocumentData(
                                row.original.idType
                              )
                            : getDocumentData(row.original.idType)
                        }
                        showDocumentLabel={true}
                        resolvedFields={_get(row.original, "resolvedFields")}
                        onlyPreview
                        addReviewComment={!isViewMode}
                        handleSaveComment={handleSaveComment}
                        formName="uploadDocuments"
                        formHelperKey={
                          currentTab === "stakeholderTab"
                            ? INDIVIDUALS.includes(row?.original?.idType)
                              ? "individualOfficial"
                              : "corporateOfficial"
                            : "beneficialOwner"
                        }
                        updateEffectKey={companyOfficialEffectKey}
                        updateEffectPath={officialSliceName}
                        formId={_get(row.original, "_id")}
                        resolveAllFieldsCheckbox
                        fileUploadComments={fileUploadComments}
                      />
                    </div>
                  );
                }}
              </Formik>
            </div>
          </>
        )}
        {selectedTab.id === "changeDocuments" && (
          <ChangeDocuments
            row={row}
            isViewMode={isViewMode}
            handleSaveComment={handleSaveComment}
            companyOfficialEffectKey={companyOfficialEffectKey}
            officialSliceName={officialSliceName}
            isCompanyDashboard={isCompanyDashboard}
            changeDocumentsComments={changeDocumentsComments}
            isRegistrar={isRegistrar}
          />
        )}
        {!isChangesList && (
          <div className="flex justify-between">
            {!isFirstStep && (
              <button
                onClick={goToPrevStep}
                className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
              >
                <span className="flex items-center mr-4 h-7">
                  <ChevronLeftIcon
                    className="flex-shrink-0 w-5 h-5"
                    aria-hidden="true"
                  />
                </span>
                <p className="font-semibold">Go back</p>
              </button>
            )}
            {isLastStep ? (
              <button
                disabled={acceptingData}
                onClick={() =>
                  stakeholderComments.length === 0
                    ? acceptData()
                    : row.toggleRowExpanded()
                }
                className="flex items-center px-4 py-2 mb-2 font-semibold text-white rounded bg-green-focused mt-7 disabled:cursor-not-allowed"
              >
                {acceptingData ? (
                  <>
                    <SpinnerIcon />
                    Accepting...
                  </>
                ) : (
                  <p>
                    {isViewMode
                      ? "Close"
                      : stakeholderComments.length === 0
                      ? "Accept Data"
                      : isApplicant
                      ? "Submit for correction"
                      : "Confirm details"}
                  </p>
                )}
                {!isViewMode && !acceptingData && (
                  <span className="flex items-center ml-4 text-white h-7">
                    <ChevronRightIcon
                      className="flex-shrink-0 w-5 h-5"
                      aria-hidden="true"
                    />
                  </span>
                )}
              </button>
            ) : (
              <button
                onClick={goToNextStep}
                className="flex items-start float-right py-2 ml-auto font-semibold rounded text-primary mt-7"
              >
                <p className="font-semibold">Review next step </p>
                <span className="flex items-center ml-4 h-7">
                  <ChevronRightIcon
                    className="flex-shrink-0 w-5 h-5"
                    aria-hidden="true"
                  />
                </span>
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

export default SubRowAsync;
